import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { MotionRoot } from '@/components/generals/motion-root'
import { PageHero } from '@/components/generals/page-hero'
import { PageIntro } from '@/components/generals/page-intro'
import { InfoGrid } from '@/components/generals/info-grid'
import { NextPage } from '@/components/generals/next-page'
import { NatureSection } from '@/components/landing/nature-section'
import { DayTimeline } from '@/components/subpages/day-timeline'

import { at, library } from '@/constants/media'

import {
    natureIntroStats,
    nextPageAfter,
    pages,
    seasons
} from '@/constants/pages'

const page = pages.nature
const next = nextPageAfter('nature')

const NaturePage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title={page.seoTitle} description={page.description} ogImage={page.image.src} />

            <MotionRoot>
                <PageHero
                    index={page.index}
                    eyebrow={page.eyebrow}
                    title={page.title}
                    accent={page.accent}
                    image={page.image}
                    facts={page.facts}
                    next='nature-intro'
                />

                <PageIntro
                    id='nature-intro'
                    index='01'
                    label='The estate'
                    aside='Heath · Pine · Reservoir · Beach'
                    statement={<>Morrow is as much <em>nature reserve</em> as golf course. Two thirds of the estate will never be mown.</>}
                    body='The course was threaded through the landscape rather than laid over it. The heath has been restored, the pines left standing, the reservoir kept wild and the dunes allowed to move the way dunes do.'
                    stats={natureIntroStats}
                    images={{
                        primary: at(library.coastPeninsula, 'center 60%'),
                        primaryCaption: 'The peninsula, where the heath meets the sea'
                    }}
                />

                <NatureSection index='02' />

                <DayTimeline index='03' />

                <InfoGrid
                    id='seasons'
                    index='04'
                    label='Seasons'
                    heading={<>Four seasons, <em>no off season.</em></>}
                    intro='The coast here is mild all year. Each season simply changes which part of the estate is at its best.'
                    items={seasons}
                    tone='forest'
                    columns={4}
                />

                <NextPage
                    href={next.href}
                    index={next.index}
                    title={next.title}
                    teaser={next.teaser}
                    image={next.image}
                />
            </MotionRoot>
        </Layout>
    )
}

export default NaturePage
